import * as React from 'react';
import {
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    ListItem,
    ListItemText,
    Stack,
    Typography,
} from '@mui/material';
import { useNotify } from 'react-admin';

import {
    phaseStatusColor,
    stepStatusLabel,
} from './flow/projectFlowUtils';
import {
    useCompletionCheck,
    useCompleteProject,
} from './flow/useProjectFlow';

interface CompleteProjectDialogProps {
    open: boolean;
    projectId: number;
    onClose: () => void;
    onCompleted?: () => void;
}

export const CompleteProjectDialog: React.FC<CompleteProjectDialogProps> = ({
    open,
    projectId,
    onClose,
    onCompleted,
}) => {
    const notify = useNotify();
    const { data, isLoading, isError } = useCompletionCheck(projectId, open);
    const completeMutation = useCompleteProject();

    const handleComplete = (force: boolean) => {
        completeMutation.mutate(
            { projectId, force },
            {
                onSuccess: () => {
                    notify('Project marked as completed.', { type: 'success' });
                    onCompleted?.();
                    onClose();
                },
            },
        );
    };

    const canComplete = data?.can_complete ?? false;
    const blockers = data?.blockers ?? [];

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Complete project</DialogTitle>
            <DialogContent>
                {isLoading && (
                    <Stack alignItems="center" sx={{ py: 3 }}>
                        <CircularProgress size={24} />
                    </Stack>
                )}
                {isError && (
                    <Typography color="error">
                        Failed to load completion status. Please try again.
                    </Typography>
                )}
                {data && canComplete && (
                    <Typography>
                        All required phases are done. Mark this project as
                        complete?
                    </Typography>
                )}
                {data && !canComplete && (
                    <Stack gap={1}>
                        <Typography>
                            Some phases are still open. You can complete the
                            project anyway, but these items are not yet done:
                        </Typography>
                        <List dense>
                            {blockers.map((b) => (
                                <ListItem
                                    key={b.key}
                                    secondaryAction={
                                        <Chip
                                            size="small"
                                            label={stepStatusLabel(b.status)}
                                            color={phaseStatusColor(b.status)}
                                            variant={
                                                phaseStatusColor(b.status) ===
                                                'default'
                                                    ? 'outlined'
                                                    : 'filled'
                                            }
                                        />
                                    }
                                >
                                    <ListItemText primary={b.label} />
                                </ListItem>
                            ))}
                        </List>
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={onClose}
                    disabled={completeMutation.isPending}
                >
                    Cancel
                </Button>
                {data && canComplete && (
                    <Button
                        variant="contained"
                        color="success"
                        onClick={() => handleComplete(false)}
                        disabled={completeMutation.isPending}
                    >
                        Complete
                    </Button>
                )}
                {data && !canComplete && (
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={() => handleComplete(true)}
                        disabled={completeMutation.isPending}
                    >
                        Complete anyway
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};
